import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { useId } from '../../../hooks';
import {
    Inline,
    Spacer,
    SIZES_WITH_RESPONSIVE_OPTION,
} from '../../sharedComponents/LayoutPrimitives';
import Tooltip from '../../sharedComponents/Tooltip';
import RadioPill from './RadioPill';
import { Placeholder } from '../Placeholder';

export default function RadioPillGroup({
    id: userId,
    description,
    isDescriptionVisible = true,
    options = [],
    value,
    onChange = () => {},
    className,
    isInvalid,
    isDisabled,
    isRequired,
    onBlur,
    space = 'xs',
    errorText,
    isLoading,
    ...rest
}) {
    const id = useId(userId);

    return (
        <div
            data-component="RadioPillGroup"
            className={cx(className, {
                invalid: isInvalid,
                disabled: isDisabled,
            })}
            {...rest}
        >
            {description && (
                <>
                    <div
                        data-radio-pill-description
                        className={cx({ 'rec-sr-only': !isDescriptionVisible })}
                        id={`${id}-description`}
                    >
                        {description}
                    </div>
                    <Spacer size={space} />
                </>
            )}

            <div data-input-wrapper>
                <Inline space={space}>
                    {isLoading && (
                        <>
                            <Placeholder height="3.2rem" width="9rem" />
                            <Placeholder height="3.2rem" width="10rem" />
                            <Placeholder height="3.2rem" width="8rem" />
                        </>
                    )}
                    {!isLoading &&
                        options.map((option, index) => {
                            if (option.tooltip && !isDisabled) {
                                return (
                                    <Tooltip content={option.tooltip} key={option.value}>
                                        <RadioPill
                                            id={`${id}-${index}`}
                                            name={`${id}-group-name`}
                                            required={isRequired}
                                            isDisabled={isDisabled || option.isDisabled}
                                            isInvalid={isInvalid}
                                            value={option.value}
                                            isChecked={value === option.value}
                                            aria-describedby={cx({
                                                [`${id}-error`]: !!errorText,
                                                [`${id}-description`]: !!description,
                                            })}
                                            onChange={(event) =>
                                                onChange(event.target.value)
                                            }
                                            onBlur={onBlur}
                                        >
                                            {option.label}
                                        </RadioPill>
                                    </Tooltip>
                                );
                            }

                            return (
                                <RadioPill
                                    key={option.value}
                                    id={`${id}-${index}`}
                                    name={`${id}-group-name`}
                                    required={isRequired}
                                    isDisabled={isDisabled || option.isDisabled}
                                    isInvalid={isInvalid}
                                    value={option.value}
                                    isChecked={value === option.value}
                                    aria-describedby={cx({
                                        [`${id}-error`]: !!errorText,
                                        [`${id}-description`]: !!description,
                                    })}
                                    onChange={(event) => onChange(event.target.value)}
                                    onBlur={onBlur}
                                >
                                    {option.label}
                                </RadioPill>
                            );
                        })}
                </Inline>
            </div>
            <div
                id={`${id}-error`}
                className="sarsa-field-error-message"
                aria-live="assertive"
            >
                {errorText && (
                    <div className="mt-1">
                        <span className="req" aria-hidden="true">
                            *
                        </span>
                        {errorText}
                    </div>
                )}
            </div>
        </div>
    );
}

RadioPillGroup.propTypes = {
    /** Visual and/or 508 label for the radio group */
    description: PropTypes.string,

    /** If true, the description is visible and displays like an input label */
    isDescriptionVisible: PropTypes.bool,

    /** Id used to construct the input ids */
    id: PropTypes.string,

    /** Array describing the pills to render */
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.node,
            isDisabled: PropTypes.bool,
            tooltip: PropTypes.node,
        })
    ),

    /** The radio that is selected. Must also include the onChange prop */
    value: PropTypes.string,

    /** Change handler. Must also include the value prop */
    onChange: PropTypes.func,

    /** Blur event handler */
    onBlur: PropTypes.func,

    /** Additional class name added to the outer container */
    className: PropTypes.string,

    /** Disables all pills */
    isDisabled: PropTypes.bool,

    /** Marks the group as invalid */
    isInvalid: PropTypes.bool,

    /** Error text to display under the group */
    errorText: PropTypes.node,

    /** Marks the group as required */
    isRequired: PropTypes.bool,

    /** Visual space between the pills */
    space: SIZES_WITH_RESPONSIVE_OPTION,

    /** If true, renders a placeholder instead of the pills */
    isLoading: PropTypes.bool,
};
